import {
  LinkButton,
  DoubleChevronRight,
  FancyText,
  ModalInfo
} from '@/components';
import {
  Box,
  Flex,
  FlexProps,
  ArrowUpRightBoxIcon,
  Card,
  CryptoIcon,
  PercentageValue,
  Skeleton
} from '@blockanalitica/ui';
import styled, { css } from 'styled-components';
import { useApi } from '@/api';

const BannerWrapper = styled(Card)`
  background-image: url(/assets/banners/savings_right.svg);
  background-repeat: no-repeat;
  background-size: cover;
  background-position: top right;
`;

const LearnMoreLink = styled(Flex)<FlexProps>`
  ${({ theme }) => css`
    color: ${theme.colors.spark.savingsPrimary};
    gap: ${theme.space['2xs']};

    &:hover {
      color: ${theme.colors.text};
    }
  `}
`;

const Tokens = styled(Flex)<FlexProps>`
  background-image: linear-gradient(
    90deg,
    rgba(43, 79, 74, 0.2),
    rgb(43, 79, 74)
  );
  border-radius: 9999px;
  padding: ${({ theme }) => theme.space['3xs']};
`;

const TokenOverlapping = styled(Flex)<FlexProps>`
  margin-left: calc(${({ theme }) => theme.sizes['3']} / -4);

  &:first-child {
    margin: 0;
  }
`;

type DataResponse = {
  symbol: string;
  rate: string;
  underlying_address: string;
  underlying_symbol: string;
  address: string;
};

export default function SavingsVaults() {
  const [data, error] = useApi<DataResponse>({
    url: '/sparkstar/savings/vaults/sUSDS/'
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return (
      <Card variant="homeCard" flex="1">
        <Skeleton height="280px" width="100%" />
      </Card>
    );
  }

  return (
    <BannerWrapper
      flexDirection={['column', 'row']}
      gap="m"
      paddingBottom="m"
      paddingTop="m"
      justifyContent="space-between">
      <Flex flex="1" flexDirection="column" gap="xs" maxWidth="40rem">
        <Box
          fontSize="6"
          alignItems="center"
          // @ts-ignore
          display="inline">
          Deposit your stablecoins and earn{' '}
          <FancyText as="span" variant="savingsApy">
            <PercentageValue value={data.rate} />
          </FancyText>{' '}
          APY!
          <Box
            // @ts-ignore
            display="inline"
            paddingLeft="2xs">
            <ModalInfo title="">
              <Flex flexDirection="column" gap="2xs">
                <Box>
                  Current annual interest in the Sky Savings Module. It is
                  determined on-chain by the Sky Ecosystem Governance. Please
                  note that these protocol mechanisms are subject to change.
                </Box>
                <Flex
                  as="a"
                  display="inline-flex"
                  gap="3xs"
                  alignItems="center"
                  // @ts-ignore
                  href="https://docs.spark.fi/user-guides/earning-savings/susds"
                  target="_blank"
                  rel="noopener noreferrer">
                  Learn more
                  <ArrowUpRightBoxIcon size="-1" />
                </Flex>
              </Flex>
            </ModalInfo>
          </Box>
        </Box>

        <Flex paddingTop="m" paddingBottom="2xs">
          <Tokens>
            <Flex alignItems="center" gap="3xs">
              <Flex>
                <TokenOverlapping>
                  <CryptoIcon
                    name="USDS"
                    address="0xdc035d45d973e3ec169d2276ddab16f1e407384f"
                    network="ethereum"
                    size="3"
                  />
                </TokenOverlapping>
                <TokenOverlapping>
                  <CryptoIcon
                    name="USDC"
                    address="0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48"
                    network="ethereum"
                    size="3"
                  />
                </TokenOverlapping>
                <TokenOverlapping>
                  <CryptoIcon
                    name="DAI"
                    address="0x6b175474e89094c44da98b954eedeac495271d0f"
                    network="ethereum"
                    size="3"
                  />
                </TokenOverlapping>
              </Flex>
              <DoubleChevronRight size="1" />
              <CryptoIcon
                name="sUSDS"
                address="0xa3931d71877c0e7a3148cb7eb4463524fec27fbd"
                network="ethereum"
                size="3"
              />
            </Flex>
          </Tokens>
        </Flex>

        <Flex color="muted" flexDirection="column" gap="3xs">
          Deposit your stablecoins into USDS Savings to tap into the Sky Savings
          Rate, which grants you a predictable APY in USDS.
          <LearnMoreLink
            as="a"
            display="inline-flex"
            // @ts-ignore
            href="https://docs.spark.fi/user-guides/earning-savings/susds"
            target="_blank"
            rel="noopener noreferrer">
            Learn more
            <ArrowUpRightBoxIcon size="-1" />
          </LearnMoreLink>
        </Flex>
      </Flex>
      <Flex
        flex="1"
        maxWidth="20rem"
        justifyContent={['flex-start', 'flex-end']}
        alignItems="flex-end"
        paddingRight="l">
        <LinkButton
          as="a"
          // @ts-ignore
          href="https://app.spark.fi/"
          target="_blank"
          rel="noopener noreferrer"
          variant="fancy"
          alignItems="center"
          gap="2xs">
          Launch App <ArrowUpRightBoxIcon size="0" />
        </LinkButton>
      </Flex>
    </BannerWrapper>
  );
}
